import React, { FC, ReactElement, useState } from 'react';
import { StatusReason, SupplierValidationRecordStatus } from '@mortee/domain/validationSystem';
import ClickEventPropagationBlocker from '@app/components/ClickEventPropagationBlocker';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem from '@app/components/inputs/DropdownItem';
import BigModal from '@app/components/BigModal';
import { BodySmallStartTransparentBlack600, Bold18TransparentBlack900 } from '@app/components/Text';
import styled from '@emotion/styled';
import Button from '@app/components/Button';
import TransitionLoader from '@app/components/TransitionLoader';
import { Checkbox, FormControlLabel } from '@mui/material';
import { arrayWithoutValue } from '@app/utils/arrayUtils';
import { observer } from 'mobx-react';
import useAppStores from '@app/hooks/useAppStores';
import MorteeMode from '@mortee/morteeMode';

interface Props {
  status: SupplierValidationRecordStatus;
  allowMultiple: boolean;
  initialValues: StatusReason[];
  title: string;
  explanation: string;
  className?: string;
  onSelect(reasons: StatusReason[]): void;
  onNotSelect(): void;
}

const StatusReasonSelector: FC<Props> = observer((props) => {
  const { validationSystemStore } = useAppStores<MorteeMode>();

  const { status, allowMultiple, initialValues, title, explanation, className, onSelect, onNotSelect } = props;

  const reasons =
    status === SupplierValidationRecordStatus.clarificationExternal
      ? validationSystemStore.clarificationExternalReasons
      : validationSystemStore.cancelReasons;

  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>(initialValues.map((reason) => reason.id));

  function handleSubmit(): void {
    onSelect(reasons.result?.filter((reason) => selectedReasonIds.includes(reason.id)) ?? []);
  }

  function handleToggleReason(reasonId: string, checked: boolean): void {
    setSelectedReasonIds((current) => (checked ? [...current, reasonId] : arrayWithoutValue(current, reasonId)));
  }

  return (
    <ClickEventPropagationBlocker className={className}>
      <SelectReasonModal
        closeFunc={onNotSelect}
        title={<Bold18TransparentBlack900.div>{title}</Bold18TransparentBlack900.div>}
        dataTestId='status-reason-modal'
      >
        <SelectExplanationText>{explanation}</SelectExplanationText>
        <TransitionLoader loading={reasons.isInProgress()}>
          {allowMultiple ? (
            <ReasonCheckboxList
              aria-label={title}
              data-testid='select-status-reasons'
              onClick={(event): void => event.stopPropagation()}
            >
              {(reasons.result ?? []).map((reason) => (
                <ReasonCheckboxLabel
                  key={reason.id}
                  data-testid={`select-status-reason-${reason.id}`}
                  control={
                    <Checkbox
                      color='primary'
                      checked={selectedReasonIds.includes(reason.id)}
                      onChange={(event): void => handleToggleReason(reason.id, event.currentTarget.checked)}
                    />
                  }
                  label={reason.reason}
                />
              ))}
            </ReasonCheckboxList>
          ) : (
            <NakedDropdown
              name='drp-select-status-reason'
              dataTestId='drp-select-status-reason'
              accessibilityLabel={title}
              placeholder='Reason'
              isSearchable
              disablePortal
              value={selectedReasonIds[0]}
              onChange={(newValue): void => setSelectedReasonIds(newValue ? [newValue] : [])}
            >
              {
                reasons.result?.map((option) => (
                  <DropdownItem key={option.id} value={option.id} textWhenSelected={option.reason}>
                    {option.reason}
                  </DropdownItem>
                )) as ReactElement[]
              }
            </NakedDropdown>
          )}
          <FormActions>
            <Button
              id='btn-status-reason-update-status'
              onClick={handleSubmit}
              colorScheme='primary'
              disabled={!reasons.isResolved() || selectedReasonIds.length === 0}
            >
              UPDATE STATUS
            </Button>
          </FormActions>
        </TransitionLoader>
      </SelectReasonModal>
    </ClickEventPropagationBlocker>
  );
});

export default StatusReasonSelector;

const SelectReasonModal = styled(BigModal)`
  padding: 16px;
  width: 500px;
  box-shadow: var(--box-shadow-3);

  --block-padding: 0;
  --side-padding: 0;
`;

const SelectExplanationText = styled(BodySmallStartTransparentBlack600.div)`
  margin-bottom: 8px;
`;

const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 16px;
`;

const ReasonCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
`;

// Align the checkbox to the first line when a reason wraps.
const ReasonCheckboxLabel = styled(FormControlLabel)`
  align-items: flex-start;
  margin: 0;

  .MuiFormControlLabel-label {
    padding-top: 9px;
    font-size: 14px;
    line-height: 1.4;
  }
`;
